import "./Search.css";

import {
  useOutsideClickEffect,
  useResponsiveValueState,
} from "@saviynt/common";
import { Box, Icon, InputField, SidePanel } from "@saviynt/design-system";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { type AccessMapSuccessResponse, type NodeData } from "../../../types";
import { TextWrapper } from "../../TextWrapper";
import { useDebounce } from "@repo/mfe-core/host";
import useClickInsideChecker from "../../../../../hooks/use-clickinsidechecker";

export default function Search({
  data,
  isOpen,
  setIsOpen,
}: {
  data: AccessMapSuccessResponse | undefined;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const intl = useIntl();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [results, setResults] = useState<NodeData[]>([]);

  const sidepanelRef = useRef(null);
  const [mediaQueryValue, setMediaQueryValue] = useState(0);

  useOutsideClickEffect(sidepanelRef, () => setIsOpen(false), true);

  useClickInsideChecker(() => {
    setIsOpen(false);
  }, ["Nhi-Inventory-access-map-Search-SidePanel"]);

  const SCREEN_WIDTH_QUERIES = [
    { query: "(max-width: 833px)", value: 100, label: "TabletMedium" },
  ];

  useResponsiveValueState(setMediaQueryValue, SCREEN_WIDTH_QUERIES);

  const tabletMediumQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === "TabletMedium",
  );

  const isTabletMediumWidth =
    tabletMediumQuery && tabletMediumQuery.value <= mediaQueryValue;

  useEffect(() => {
    let searchResults: NodeData[] = [];

    if (debouncedSearch.length) {
      const nodes = data?.data?.nodes;

      if (nodes?.length) {
        searchResults = nodes.filter((obj) =>
          Object.values(obj).some(
            (value) =>
              typeof value === "string" &&
              String(value)
                .toLowerCase()
                .includes(debouncedSearch?.toLowerCase()),
          ),
        );
      }
    } else {
      const nodes = data?.data?.nodes;
      if (nodes?.length) {
        searchResults = nodes;
      }
    }

    setResults(searchResults);
  }, [data, debouncedSearch]);

  const sampleLayout = () => (
    <div className="Search-SidePanel-Content">
      <div className="input">
        <InputField
          name="search"
          placeholder={intl.formatMessage({
            id: "iPX0vR",
            defaultMessage: "Search",
            description: "Placeholder text for search input field",
          })}
          kind="search"
          backgroundColor="neutral"
          prefixIcon={
            <Icon kind="Search" color="--color-foreground-neutral-subtle" />
          }
          value={search}
          setValue={setSearch}
        />
      </div>
      <div className="results">
        {intl.formatMessage(
          {
            id: "slwweo",
            defaultMessage: "Results{count, plural, =0 {} other { ({count})}}",
            description:
              "Label for search results section showing the number of found items",
          },
          { count: results.length },
        )}
      </div>
      {results.length > 0 ? (
        results.map((result) => (
          <div className="content" key={result.label}>
            <TextWrapper label={result.displayName} subLabel={result.type} />
          </div>
        ))
      ) : (
        <div className="no-results">
          {intl.formatMessage({
            id: "GaG9KT",
            defaultMessage: "No results found",
            description: "Message shown when no search results are found",
          })}
        </div>
      )}
    </div>
  );

  return (
    <Box className="Nhi-Inventory-AccessMap-Search-SidePanel">
      <SidePanel
        ref={sidepanelRef}
        data-testid="search-side-panel"
        header={{
          icon: "Search",
          title: intl.formatMessage({
            description: "nhi.AccessMap.search",
            defaultMessage: "Map Search",
            id: "q0z4Nh",
          }),
        }}
        appIconSize="small"
        layout={sampleLayout()}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        isSidePanelNavigatingList={false}
        className="Example-SidePanel"
        classNames={{ layoutClassName: "Example-SidePanel-layout" }}
        isTabletWidth={isTabletMediumWidth}
      />
    </Box>
  );
}
